"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { FormattedMessage, useIntl } from "react-intl";
import { FaArrowRight } from "react-icons/fa6";
import {
  trackCtaClick,
  trackEvent,
} from "@/app/providers/ObservabilityProvider";
import { useAppLink } from "@/lib/analytics/app-links";
import { useLocale } from "@/app/providers/IntlProvider";
import { DEMO_URL } from "@/lib/demo";
import { StoryLottie, phoneStageClass } from "../Story/StoryLottie";

/**
 * Closing call to action. The picture proves the headline ("tomorrow morning, you will
 * know where to start") and nothing else: in the `morning` scene one card, the answer,
 * lands under "Start with" and takes the focus. Kept deliberately airy.
 */
export default function FinalCTASection() {
  const { href: appUrl, target: appTarget } = useAppLink("final-cta");
  const intl = useIntl();
  const { locale } = useLocale();
  const stageRef = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setNear(true);
        setInView(e.intersectionRatio >= 0.6);
      },
      { threshold: [0, 0.6], rootMargin: "40% 0px 40% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section className="w-full overflow-hidden bg-blue-main px-4 py-14 sm:px-8 sm:py-24">
      <div className="mx-auto grid max-w-6xl items-center gap-10 sm:gap-12 lg:grid-cols-2">
        <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
          <h2 className="text-balance text-3xl font-bold leading-[1.12] tracking-[-0.02em] text-white sm:text-4xl lg:text-5xl">
            <FormattedMessage id="finalCta.title" />
          </h2>
          <p className="mt-4 max-w-xl text-balance text-base text-white/85 sm:text-lg">
            <FormattedMessage id="finalCta.subtitle" />
          </p>

          <div className="mt-8 flex w-full flex-col items-stretch justify-center gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
            <Link
              href={appUrl}
              onClick={() => {
                trackEvent("finalCta-signup-button", {}, { criticalOnNav: true });
                trackCtaClick("final-cta", appTarget);
              }}
              className="group/cta flex items-center justify-center gap-2 rounded-full bg-white px-7 py-4 text-base font-semibold text-blue-main transition-transform duration-200 hover:scale-105"
            >
              <FormattedMessage id="finalCta.signup" />
              <FaArrowRight aria-hidden="true" className="shrink-0 text-xs transition-transform duration-200 group-hover/cta:translate-x-1" />
            </Link>
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackEvent("finalCta-demo-button", {}, { criticalOnNav: true });
                trackCtaClick("final-cta", "demo");
              }}
              className="flex items-center justify-center gap-2 rounded-full border border-white/60 px-7 py-4 text-base font-semibold text-white transition-colors duration-200 hover:bg-white/10"
            >
              <FormattedMessage id="finalCta.demo" />
            </a>
          </div>
        </div>

        <div
          ref={stageRef}
          aria-hidden="true"
          className={`relative aspect-[5/4] w-full overflow-hidden rounded-[32px] sm:rounded-[40px] ${phoneStageClass("morning")}`}
          style={{ background: "var(--grad-cta)" }}
        >
          <StoryLottie
            name="morning"
            locale={locale}
            load={near}
            active={inView}
            label={intl.formatMessage({ id: "finalCta.morningAlt" })}
            className="absolute inset-0"
          />
        </div>
      </div>
    </section>
  );
}
